/**
 * Landing-page media rules for Self-Serve classes (ELS-639).
 *
 * The instructions field stores S3 **object keys**, never base64 — a `TEXT` column tops out at
 * 64KB and one inlined screenshot blows past that. The editor, however, hands us whatever the
 * browser produced: a `data:` URL for a pasted image, a `blob:` URL for a picked video, or the
 * signed `https://` URL we ourselves substituted the last time the form was loaded.
 *
 * Deciding which of those three a `src` is, is pure string work, so it lives here rather than in
 * the service that does the uploading.
 *
 * @see docs/edsims/modules/21-self-serve-enrollment.md
 */

export const IMAGE_KEY_PREFIX = "selfServeClasses/images/";
export const VIDEO_KEY_PREFIX = "selfServeClasses/videos/";

export type MediaKind = "image" | "video";

export const keyPrefix = (kind: MediaKind): string =>
  kind === "image" ? IMAGE_KEY_PREFIX : VIDEO_KEY_PREFIX;

/**
 * What has to happen to a media `src` before the HTML can be saved.
 *
 * - `upload` — still local to the browser, so the bytes are not in S3 yet.
 * - `normalize` — a signed URL we handed the editor; strip it back to the object key.
 * - `keep` — already a bare object key.
 * - `skip` — empty, or something we do not recognise (an external image the admin linked to,
 *   which we must not try to upload or rewrite).
 */
export type MediaSrcAction = "upload" | "normalize" | "keep" | "skip";

/** Bytes the browser is holding: a pasted/base64 image or a picked video's preview URL. */
export const isLocalSrc = (src: string | null | undefined): boolean =>
  !!src && (src.startsWith("data:") || src.startsWith("blob:"));

/** A bare stored key, e.g. `selfServeClasses/images/uuid.png`. */
export const isObjectKey = (
  src: string | null | undefined,
  kind: MediaKind
): boolean => !!src && src.startsWith(keyPrefix(kind));

/**
 * Reduces a signed S3 view URL back to the object key we store.
 *
 * Returns `null` when the URL carries no key for this kind, so callers leave it alone instead of
 * writing a half-parsed value into the column.
 */
export const toObjectKey = (
  src: string | null | undefined,
  kind: MediaKind
): string | null => {
  if (!src) {
    return null;
  }
  // Everything up to the query string, which is where the signature lives.
  const match = src.match(new RegExp(`${keyPrefix(kind)}([A-Za-z0-9._\\-]+)`));
  return match ? match[0] : null;
};

export const mediaSrcAction = (
  src: string | null | undefined,
  kind: MediaKind
): MediaSrcAction => {
  if (!src) {
    return "skip";
  }
  if (isLocalSrc(src)) {
    return "upload";
  }
  if (isObjectKey(src, kind)) {
    return "keep";
  }
  return toObjectKey(src, kind) ? "normalize" : "skip";
};

/**
 * Rewrites one `src` for storage, or returns `null` when the caller must upload first.
 *
 * Kept separate from {@link mediaSrcAction} so the service can branch on the action it needs to
 * perform (the upload is asynchronous) while the rewrite itself stays synchronous and testable.
 */
export const srcForStorage = (
  src: string | null | undefined,
  kind: MediaKind
): string | null => {
  switch (mediaSrcAction(src, kind)) {
    case "keep":
      return src as string;
    case "normalize":
      return toObjectKey(src, kind);
    default:
      return null;
  }
};
